import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/use-toast';
import { Upload, Download, AlertCircle, CheckCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PersonFinal } from '@/api/entities';
import { Progress } from '@/components/ui/progress';

export default function SocialPresenceUploader() {
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState(null);
  const { toast } = useToast();

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setUploadResults(null);
    } else {
      toast({
        title: 'Invalid file',
        description: 'Please select a CSV file',
        variant: 'destructive'
      });
    }
  };

  const downloadTemplate = () => {
    const csv = 'tenant_id,person_id,social_presence_score\ninstructure,68d15d1120b1866c83c0e871,85\ninstructure,68d15d1120b1866c83c0e872,92\ncalmedica,68d15d1120b1866c83c0e873,78';
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'social_presence_template.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  };

  const handleUpload = async () => {
    if (!file) {
      toast({
        title: 'No file selected',
        description: 'Please select a CSV file to upload',
        variant: 'destructive'
      });
      return;
    }

    setIsUploading(true);
    setProgress(0);
    setUploadResults(null);

    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      const headers = lines[0].split(',').map(h => h.trim());
      
      if (headers[0] !== 'tenant_id' || headers[1] !== 'person_id' || headers[2] !== 'social_presence_score') {
        throw new Error('CSV must have columns: tenant_id, person_id, social_presence_score (in that order)');
      }

      const parseCSVLine = (line) => {
        const result = [];
        let current = '';
        let inQuotes = false;
        
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            result.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        result.push(current.trim());
        return result;
      };

      const allPeople = await PersonFinal.list();
      const peopleById = new Map();
      
      allPeople.forEach(p => {
        const id = p.person_id || p.id;
        if (id) {
          peopleById.set(id, p);
        }
      });

      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      toast({
        title: 'Processing...',
        description: `Updating ${lines.length - 1} personas with social presence scores`,
        duration: 10000
      });

      for (let i = 1; i < lines.length; i++) {
        try {
          const values = parseCSVLine(lines[i]);
          const tenantId = values[0]?.trim();
          const personId = values[1]?.trim();
          const socialScore = values[2]?.trim();

          if (!tenantId) {
            console.warn(`Row ${i + 1}: Missing tenant_id, skipping`);
            errorCount++;
            errors.push(`Row ${i + 1}: Missing tenant_id`);
            continue;
          }

          if (!personId) {
            console.warn(`Row ${i + 1}: Missing person_id, skipping`);
            errorCount++;
            errors.push(`Row ${i + 1}: Missing person_id`);
            continue;
          }

          if (!socialScore || isNaN(parseFloat(socialScore))) {
            console.warn(`Row ${i + 1}: Invalid social_presence_score, skipping`);
            errorCount++;
            errors.push(`Row ${i + 1}: Invalid social_presence_score`);
            continue;
          }

          const score = parseFloat(socialScore);
          if (score < 0 || score > 100) {
            console.warn(`Row ${i + 1}: Score must be 0-100, got ${score}`);
            errorCount++;
            errors.push(`Row ${i + 1}: Score must be 0-100 (got ${score})`);
            continue;
          }

          const person = peopleById.get(personId);
          
          if (!person) {
            console.warn(`Row ${i + 1}: Person not found with ID: ${personId}`);
            errorCount++;
            errors.push(`Row ${i + 1}: Person not found: ${personId}`);
            continue;
          }

          await PersonFinal.update(person.id, {
            social_presence_score: score
          });

          successCount++;
          setProgress(Math.round((i / (lines.length - 1)) * 100));

          if (i < lines.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 700));
          }

          if (successCount % 10 === 0) {
            toast({
              title: 'Progress...',
              description: `Updated ${successCount} of ${lines.length - 1} personas`,
              duration: 3000
            });
          }
        } catch (error) {
          console.error(`Error processing row ${i + 1}:`, error);
          errorCount++;
          errors.push(`Row ${i + 1}: ${error.message}`);
          
          if (error.response?.status === 429) {
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }
      }

      setFile(null);
      setProgress(100);
      setUploadResults({ successCount, errorCount, errors });
      
      if (errorCount === 0) {
        toast({
          title: 'Success!',
          description: `Updated ${successCount} personas with social presence scores`,
          duration: 5000
        });
      } else {
        toast({
          title: 'Partially completed',
          description: `${successCount} succeeded, ${errorCount} failed`,
          variant: 'default',
          duration: 7000
        });
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload failed',
        description: error.message || 'An error occurred during upload',
        variant: 'destructive'
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="w-5 h-5" />
          Social Presence Score Uploader
        </CardTitle>
        <CardDescription>
          Upload social presence scores for personas via CSV
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>CSV Format (3 columns):</strong>
            <br />
            • Column 1: <code className="bg-gray-100 px-1 rounded">tenant_id</code> (e.g., instructure, calmedica, demarque)
            <br />
            • Column 2: <code className="bg-gray-100 px-1 rounded">person_id</code> (exact match required)
            <br />
            • Column 3: <code className="bg-gray-100 px-1 rounded">social_presence_score</code> (number 0-100)
            <br />
            <br />
            <strong>Example:</strong>
            <br />
            <code className="bg-gray-100 px-1 rounded text-xs">
              instructure,68d15d1120b1866c83c0e871,85
            </code>
          </AlertDescription>
        </Alert>

        <div className="space-y-2">
          <Label>Download Template</Label>
          <Button onClick={downloadTemplate} variant="outline" className="w-full">
            <Download className="w-4 h-4 mr-2" />
            Download CSV Template
          </Button>
        </div>

        <div className="space-y-2">
          <Label htmlFor="file">Upload CSV</Label>
          <Input
            id="file"
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            disabled={isUploading}
          />
          {file && (
            <p className="text-sm text-gray-600">
              Selected: {file.name}
            </p>
          )}
        </div>

        {isUploading && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-600">
              <span>Uploading...</span>
              <span>{progress}%</span>
            </div>
            <Progress value={progress} className="w-full" />
          </div>
        )}

        {uploadResults && (
          <Alert className={uploadResults.errorCount === 0 ? 'border-green-200 bg-green-50' : 'border-yellow-200 bg-yellow-50'}>
            {uploadResults.errorCount === 0 ? (
              <CheckCircle className="h-4 w-4 text-green-600" />
            ) : (
              <AlertCircle className="h-4 w-4 text-yellow-600" />
            )}
            <AlertDescription>
              <strong>Upload Complete:</strong>
              <br />
              ✅ {uploadResults.successCount} personas updated successfully
              {uploadResults.errorCount > 0 && (
                <>
                  <br />
                  ❌ {uploadResults.errorCount} errors
                  {uploadResults.errors.length > 0 && (
                    <div className="mt-2 max-h-40 overflow-y-auto">
                      <details className="text-xs">
                        <summary className="cursor-pointer font-medium">View errors</summary>
                        <ul className="mt-2 space-y-1 list-disc list-inside">
                          {uploadResults.errors.slice(0, 20).map((error, idx) => (
                            <li key={idx}>{error}</li>
                          ))}
                          {uploadResults.errors.length > 20 && (
                            <li>... and {uploadResults.errors.length - 20} more</li>
                          )}
                        </ul>
                      </details>
                    </div>
                  )}
                </>
              )}
            </AlertDescription>
          </Alert>
        )}

        <Button
          onClick={handleUpload}
          disabled={!file || isUploading}
          className="w-full"
        >
          {isUploading ? (
            'Uploading...'
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Upload Social Presence Scores
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}